<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, inject, onMounted, ref, shallowRef } from 'vue'
import { storeToRefs } from 'pinia'
import { LoaderCircle, Ticket } from 'lucide-vue-next'
import { addMinutes } from 'date-fns'
import Footer from '@/components/Footer.vue'
import { useAuthStore } from '@/stores/authStore'
import Tools from '@/utils/tools'
import type { RequestRouter } from '@/request/requestRouter'
import { RequestAction } from '@/request/requestRouter'
import router from '@/router'

interface Props {

}

defineProps<Props>()

const { t } = useI18n()

const { user } = storeToRefs(useAuthStore())
const request = inject<RequestRouter>('request')
const claiming = shallowRef<boolean>(false)
const dailyDone = shallowRef<boolean>(false)
const taskIndex = shallowRef<number>(1)

const tasks = [
	'reddit',
	'discord',
]

const task = computed(() => {
	return tasks[taskIndex.value]
})

function claim() {
	if (user.value.daily_quest_completed) {
		router.push('/tickets')
		return
	}
	claiming.value = true

	if (task.value === 'reddit') {
		window.open('https://www.reddit.com/r/FapFapAI/')
	}
	else if (task.value === 'discord') {
		window.open(import.meta.env.VITE_DISCORD_INVITE_LINK)
	}

	setTimeout(() => {
		claiming.value = false
		dailyDone.value = true
		user.value.daily_quest_completed = true
		user.value.generation_token += 10
		request?.exec(RequestAction.DailyQuest)
	}, 60000)
}

const cookie = Tools.getCookie('daily_task')
if (!cookie) {
	taskIndex.value = Tools.random(0, tasks.length - 1)
	const expiryDate = addMinutes(new Date(), 30)
	Tools.setCookie('daily_task', taskIndex.value.toString(), expiryDate, `.${import.meta.env.VITE_DOMAIN}`, '/')
}
else {
	taskIndex.value = Number(cookie)
}
</script>

<template>
	<div class="flex flex-col gap-4 items-center flex-nowrap menu:mt-[70px] mt-[120px] w-full min-h-screen h-screen">
		<div class="relative mx-auto sm:w-[500px] w-screen px-4 sm:px-0 mt-8">
			<img
				src="/image/daily_tasks/quest_cropped.png"
				width="100%"
				class="rounded-xl"
			>
			<div
				class="top-[21%] left-[72%] translate-x-[-50%] translate-y-[-50%] whitespace-nowrap absolute flex items-center gap-2 text-black text-base sm:text-lg tracking-tight"
			>
				<template v-if="!user.daily_quest_completed">
					<i
						:class="t(`daily_tasks.${task}.icon`)"
						class="text-2xl sm:text-4xl"
					/>
					{{ t(`daily_tasks.${task}.title`) }}
				</template>
				<template v-else>
					{{ t('daily_tasks.hello_there') }}
				</template>
			</div>
		</div>

		<div class="space-y-2 w-full sm:w-[500px] px-4">
			<template v-if="!user.daily_quest_completed">
				<div
					v-for="i in 3"
					:key="i - 1"
					class="group relative flex items-center gap-2 p-2 bg-slate-100 rounded-xl border border-purple-100"
				>
					<!-- Step number -->
					<div class="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
						{{ i }}
					</div>
					<div class="flex-1 min-w-0">
						<span class="block text-gray-700 font-medium leading-relaxed text-sm sm:text-base transition-colors duration-200">
							{{ t(`daily_tasks.${task}.step.${i - 1}`) }}
						</span>
					</div>
				</div>
			</template>
			<div
				v-else-if="dailyDone"
				class="flex flex-col gap-1 leading-relaxed"
			>
				<span class="sm:whitespace-nowrap text-xl font-bold tracking-tight">{{ t('daily_tasks.task_completed_title') }}</span>
				{{ t('daily_tasks.task_completed_desc', { count: 10 }) }}
			</div>
			<div
				v-else
				class="flex flex-col gap-1 leading-relaxed"
			>
				<span class="sm:whitespace-nowrap text-xl font-bold tracking-tight">	{{ t('daily_tasks.wait_until_tomorrow_title') }}</span>
				{{ t('daily_tasks.wait_until_tomorrow_desc') }}
			</div>

			<Button
				outlined
				:class="{
					'!mt-6': !user.daily_quest_completed,
					'button-rainbow-shade': !user.daily_quest_completed,
					'button-yellow-shade': user.daily_quest_completed,
				}"
				class="w-full !text-lg md:!text-xl tracking-tight"
				:disabled="claiming"
				@click="claim"
			>
				<template #default>
					<span
						v-if="claiming"
						class="flex-center gap-1"
					>
						<LoaderCircle class="animate-spin" />
						{{ t('generator.tasks.-1') }}
					</span>
					<span
						v-else-if="!user.daily_quest_completed"
						class="flex items-center gap-1"
					>
						{{ t('daily_tasks.claim_tickets') }} 10<Ticket class="lucide-button-icon-md" />
					</span>
					<span
						v-else
						class="flex-center gap-1 text-black"
					>
						{{ t('common.upgrade') }}
					</span>
				</template>
			</Button>
		</div>
	</div>
	<Footer />
</template>

<style lang="scss" scoped>
.fade-up-enter-active {
	transition: all 0.2s ease;
}
.fade-up-enter-from {
	opacity: 0;
	transform: translateY(10px);
}
</style>
